import React, { useEffect, useRef, useState, useCallback } from "react";
import { authHeaders } from "../auth";
import { speakText, stopSpeaking } from "../voice";
import SiraguLogo from "./SiraguLogo";
import "./VoiceMode.css";

/* ── VoiceMode ──────────────────────────────────────────────────────────────
   Hands-free, ChatGPT-style continuous voice conversation.

   The loop, driven entirely by refs (so async callbacks always read live state):
     listening → (silence detected) → thinking → speaking → listening → …

   1. LISTEN   MediaRecorder captures the mic. A Web Audio AnalyserNode measures
               RMS level each animation frame; once speech has been heard and the
               user then falls silent for ~1.4s, we stop the recorder.
   2. TRANSCRIBE  The recorded Blob is POSTed to /transcribe (field "file").
   3. SEND     The transcript is handed to App's onSend — the SAME pipeline typed
               messages use (same session_id, same append/logging). It returns the
               assistant's spoken reply text.
   4. SPEAK    speakText() reads the reply in the shared Indian-female voice; on
               end we resume listening automatically.
   BARGE-IN    While speaking we keep a light mic monitor running; if the user
               starts talking we stopSpeaking() and jump back to listening.
   ──────────────────────────────────────────────────────────────────────────── */

const SILENCE_RMS = 0.015;    // below this level counts as silence
const SILENCE_MS = 1400;      // stop after this much continuous silence
const MIN_SPEAK_MS = 400;     // ignore the very first moment (avoid instant cutoff)
const BARGE_RMS = 0.05;       // louder threshold to trigger barge-in while speaking

function VoiceMode({ apiBase, onSend, onClose }) {
  // "idle" | "listening" | "thinking" | "speaking" | "error"
  const [status, setStatus] = useState("idle");
  const [muted, setMuted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [transcript, setTranscript] = useState(""); // last thing the user said

  // Live refs — async callbacks (recorder.onstop, rAF ticks, TTS onend) must not
  // close over stale React state.
  const activeRef = useRef(true);        // false once we've torn down / closed
  const mutedRef = useRef(false);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const rafRef = useRef(null);

  const setStatusSafe = useCallback((s) => {
    if (activeRef.current) setStatus(s);
  }, []);

  /* ── Teardown helpers ──────────────────────────────────────────────────── */
  const stopRaf = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const closeAudioCtx = useCallback(() => {
    if (sourceRef.current) {
      try { sourceRef.current.disconnect(); } catch (_) {}
      sourceRef.current = null;
    }
    analyserRef.current = null;
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (_) {}
      audioCtxRef.current = null;
    }
  }, []);

  const teardownRecorder = useCallback(() => {
    stopRaf();
    const mr = recorderRef.current;
    if (mr && mr.state !== "inactive") {
      try { mr.onstop = null; mr.stop(); } catch (_) {}
    }
    recorderRef.current = null;
    closeAudioCtx();
    stopStream();
  }, [stopRaf, closeAudioCtx, stopStream]);

  /* ── Set up a shared Web Audio analyser over the current mic stream ─────── */
  const ensureAnalyser = useCallback((stream) => {
    if (analyserRef.current) return analyserRef.current;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    const ctx = new Ctx();
    audioCtxRef.current = ctx;
    const source = ctx.createMediaStreamSource(stream);
    sourceRef.current = source;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
    analyserRef.current = analyser;
    return analyser;
  }, []);

  const readRms = useCallback((analyser, buf) => {
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      sum += v * v;
    }
    return Math.sqrt(sum / buf.length);
  }, []);

  /* ── SPEAK: read the reply, then resume listening on end ───────────────── */
  const speakReply = useCallback((text) => {
    if (!activeRef.current) return;
    if (!text || mutedRef.current) {
      // Nothing to say (or muted) — go straight back to listening.
      startListening();
      return;
    }
    setStatusSafe("speaking");

    // Keep a light monitor running for barge-in while the assistant talks.
    const analyser = analyserRef.current;
    let bargeBuf = analyser ? new Uint8Array(analyser.fftSize) : null;
    const monitorBarge = () => {
      if (!activeRef.current || !analyserRef.current || !bargeBuf) return;
      const rms = readRms(analyserRef.current, bargeBuf);
      if (rms >= BARGE_RMS) {
        stopSpeaking();
        stopRaf();
        startListening(); // user interrupted → listen again
        return;
      }
      rafRef.current = requestAnimationFrame(monitorBarge);
    };
    if (analyser) rafRef.current = requestAnimationFrame(monitorBarge);

    speakText(text, {
      onend: () => {
        stopRaf();
        if (!activeRef.current) return;
        startListening();
      },
      onerror: () => {
        stopRaf();
        if (!activeRef.current) return;
        startListening();
      },
    });
  }, [readRms, setStatusSafe, stopRaf]);

  /* ── TRANSCRIBE + SEND ─────────────────────────────────────────────────── */
  const handleBlob = useCallback(async (blob) => {
    if (!activeRef.current) return;
    setStatusSafe("thinking");
    try {
      const form = new FormData();
      form.append("file", blob, "recording.webm");
      const res = await fetch(`${apiBase}/transcribe`, {
        method: "POST",
        headers: { ...authHeaders() },
        body: form,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `Transcription failed (${res.status})`);
      }
      const data = await res.json();
      const text = (data.text || "").trim();

      if (!activeRef.current) return;

      if (!text) {
        // Didn't catch anything — just listen again.
        startListening();
        return;
      }

      setTranscript(text);

      // Reuse App's exact chat pipeline (same session_id, append + logging).
      const reply = await onSend(text);
      if (!activeRef.current) return;
      speakReply(reply || "");
    } catch (err) {
      if (!activeRef.current) return;
      setErrorMsg(err.message || "Something went wrong.");
      setStatusSafe("error");
    }
  }, [apiBase, onSend, speakReply, setStatusSafe]);

  /* ── LISTEN: record until silence, then hand the blob off ──────────────── */
  const startListening = useCallback(async () => {
    if (!activeRef.current || mutedRef.current) return;
    stopRaf();
    setErrorMsg("");
    setStatusSafe("listening");

    try {
      // Reuse an existing stream if we still hold one, else request the mic.
      let stream = streamRef.current;
      if (!stream || !stream.active) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
      }
      if (!activeRef.current) { stopStream(); return; }

      const analyser = ensureAnalyser(stream);
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      recorderRef.current = mr;
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        stopRaf();
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size > 0) handleBlob(blob);
        else if (activeRef.current && !mutedRef.current) startListening();
      };
      mr.start();

      // Silence detection over the analyser.
      if (analyser) {
        const buf = new Uint8Array(analyser.fftSize);
        const startedAt = performance.now();
        let silenceStart = null;
        let heardSpeech = false;
        const tick = () => {
          if (!activeRef.current || recorderRef.current !== mr) return;
          const rms = readRms(analyser, buf);
          const now = performance.now();
          if (rms >= SILENCE_RMS) {
            heardSpeech = true;
            silenceStart = null;
          } else if (heardSpeech && now - startedAt > MIN_SPEAK_MS) {
            if (silenceStart === null) silenceStart = now;
            else if (now - silenceStart > SILENCE_MS) {
              if (mr.state !== "inactive") mr.stop();
              return;
            }
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      }
    } catch (err) {
      if (!activeRef.current) return;
      setErrorMsg(
        err && err.name === "NotAllowedError"
          ? "Microphone access denied. Please allow the mic and try again."
          : "Couldn't access the microphone."
      );
      setStatusSafe("error");
    }
  }, [ensureAnalyser, handleBlob, readRms, setStatusSafe, stopRaf, stopStream]);

  /* ── Mount: kick off the loop. Unmount: full cleanup. ──────────────────── */
  useEffect(() => {
    activeRef.current = true;
    startListening();
    return () => {
      activeRef.current = false;
      stopSpeaking();
      teardownRecorder();
    };
  }, []);

  /* ── Controls ──────────────────────────────────────────────────────────── */
  const handleEnd = () => {
    activeRef.current = false;
    stopSpeaking();
    teardownRecorder();
    onClose && onClose();
  };

  const toggleMute = () => {
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);
    if (next) {
      // Pause: stop speaking + recording, idle out.
      stopSpeaking();
      teardownRecorder();
      setStatusSafe("idle");
    } else {
      startListening();
    }
  };

  const retry = () => {
    setErrorMsg("");
    startListening();
  };

  const statusLabel =
    status === "listening" ? "Listening…"
    : status === "thinking" ? "Thinking…"
    : status === "speaking" ? "Speaking…"
    : status === "error" ? "Something went wrong"
    : muted ? "Paused"
    : "Starting…";

  return (
    <div className="voicemode-overlay" role="dialog" aria-label="Voice conversation">
      <div className="voicemode-inner">
        <div className={`voicemode-orb vm-${status}${muted ? " vm-muted" : ""}`}>
          <span className="voicemode-orb-glow" />
          <span className="voicemode-orb-ring voicemode-orb-ring-1" />
          <span className="voicemode-orb-ring voicemode-orb-ring-2" />
          <span className="voicemode-orb-ring voicemode-orb-ring-3" />
          <span className="voicemode-orb-logo">
            <SiraguLogo
              size={96}
              active={status === "listening" || status === "speaking"}
            />
          </span>
        </div>

        <div className="voicemode-status" aria-live="polite">{statusLabel}</div>

        {transcript && status !== "error" && (
          <div className="voicemode-transcript">"{transcript}"</div>
        )}

        {errorMsg && (
          <div className="voicemode-error">
            {errorMsg}
            <button type="button" className="voicemode-retry" onClick={retry}>
              Try again
            </button>
          </div>
        )}

        <div className="voicemode-controls">
          <button
            type="button"
            className={`voicemode-mute${muted ? " active" : ""}`}
            onClick={toggleMute}
            aria-label={muted ? "Resume listening" : "Pause / mute"}
            title={muted ? "Resume" : "Pause"}
          >
            {muted ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M8 5v14l-5-4H1V9h2l5-4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                <path d="M16 9l6 6M22 9l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z"
                  stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                <path d="M6 11v1a6 6 0 0 0 12 0v-1M12 18v3"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>

          <button
            type="button"
            className="voicemode-end"
            onClick={handleEnd}
            aria-label="End voice conversation"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
            </svg>
            <span>End</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default VoiceMode;
